import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { UserCheck, Users, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { EmployeeAvatar } from '@/components/hr/employee-avatar'
import { ModuleGuard } from '@/components/module-guard'
import { orpc } from '@/lib/orpcClient'
import type { HREmployee } from '@cloud/db'

export const Route = createFileRoute('/hr/onboarding')({
  component: OnboardingPage,
})

function OnboardingPage() {
  return <ModuleGuard moduleKey="hr"><OnboardingDashboard /></ModuleGuard>
}

function OnboardingDashboard() {
  const { data, isLoading } = useQuery(orpc.hr.getOnboardingOverview.queryOptions())

  const employees = (data ?? []) as (HREmployee & {
    totalTasks: number
    completedTasks: number
    completionPercentage: number
  })[]

  const totalOnboarding = employees.length
  const averageCompletion =
    totalOnboarding > 0
      ? Math.round(
          employees.reduce((sum, e) => sum + e.completionPercentage, 0) / totalOnboarding,
        )
      : 0
  const completedCount = employees.filter((e) => e.completionPercentage === 100).length

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Onboarding Dashboard</h1>
        <p className="text-muted-foreground">Track employee onboarding progress</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Employees Onboarding</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOnboarding}</div>
            <p className="text-xs text-muted-foreground">Active onboardings</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Average Completion</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageCompletion}%</div>
            <Progress value={averageCompletion} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedCount}</div>
            <p className="text-xs text-muted-foreground">
              {totalOnboarding > 0
                ? `${Math.round((completedCount / totalOnboarding) * 100)}% of total`
                : '0% of total'}
            </p>
          </CardContent>
        </Card>
      </div>

      {employees.length === 0 ? (
        <div className="text-center py-12">
          <UserCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No employees currently onboarding</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee) => (
            <Link key={employee.id} to="/hr/employees/$employeeId" params={{ employeeId: String(employee.id) }}>
            <Card
              className="cursor-pointer hover:shadow-md transition-shadow"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <EmployeeAvatar employee={employee} />
                  <div className="flex-1">
                    <p className="font-medium">
                      {employee.firstName} {employee.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground">{employee.jobTitle ?? "-"}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span className="font-medium">{employee.completionPercentage}%</span>
                  </div>
                  <Progress value={employee.completionPercentage} className="h-2" />
                  <p className="text-xs text-muted-foreground text-right">
                    {employee.completedTasks}/{employee.totalTasks} tasks
                  </p>
                </div>
                {employee.completionPercentage === 100 && (
                  <Badge variant="default" className="mt-3 bg-green-500">
                    Completed
                  </Badge>
                )}
              </CardContent>
            </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
